import React, { useEffect } from 'react';

interface SEOProps {
    title: string;
    description: string;
    keywords?: string;
    image?: string;
    url?: string;
    type?: 'website' | 'article' | 'profile';
    jsonLd?: object | object[];
}

export const SEO: React.FC<SEOProps> = ({ 
    title, 
    description, 
    keywords, 
    image: rawImage = '/icon.png', 
    url = window.location.href,
    type = 'website',
    jsonLd
}) => {
    useEffect(() => {
        // Facebook/WhatsApp/X exigen URL absoluta en og:image — con "/icon.png"
        // la vista previa al compartir salía sin imagen.
        // Varias páginas ya incluyen "| Amigo Peludo" en su título: se evita repetirlo.
        const fullTitle = /amigo peludo/i.test(title) ? title : `${title} | Amigo Peludo`;
        const image = rawImage.startsWith('/') ? `https://amigopeludo.cl${rawImage}` : rawImage;

        // --- Standard Tags ---
        document.title = fullTitle;
        
        const updateMeta = (attr: string, name: string, content: string) => {
            let el = document.querySelector(`meta[${attr}="${name}"]`);
            if (!el) {
                el = document.createElement('meta');
                el.setAttribute(attr, name);
                document.head.appendChild(el);
            }
            el.setAttribute('content', content);
        };

        updateMeta('name', 'description', description);
        updateMeta('name', 'keywords', keywords || 'mascotas, perros, gatos, mascotas perdidas, reportar, SOS, comunidad');

        // --- Open Graph & Twitter ---
        const socialTags = [
            { p: 'og:title', c: fullTitle },
            { p: 'og:description', c: description },
            { p: 'og:image', c: image },
            { p: 'og:url', c: url },
            { p: 'og:type', c: type },
            { p: 'og:site_name', c: 'Amigo Peludo' },
            { n: 'twitter:card', c: 'summary_large_image' },
            { n: 'twitter:site', c: '@amigopeludo_oficial' },
            { n: 'twitter:title', c: fullTitle },
            { n: 'twitter:description', c: description },
            { n: 'twitter:image', c: image }
        ];

        socialTags.forEach(t => {
            if (t.p) {
                let el = document.querySelector(`meta[property="${t.p}"]`);
                if (!el) {
                    el = document.createElement('meta');
                    el.setAttribute('property', t.p);
                    document.head.appendChild(el);
                }
                el.setAttribute('content', t.c);
            } else if (t.n) {
                updateMeta('name', t.n, t.c);
            }
        });

        // --- Advanced JSON-LD ---
        let existingScripts = document.querySelectorAll('script[id^="json-ld-"]');
        existingScripts.forEach(s => s.remove());

        const createJsonLd = (id: string, data: object) => {
            const sc = document.createElement('script');
            sc.id = `json-ld-${id}`;
            sc.type = 'application/ld+json';
            sc.innerHTML = JSON.stringify(data);
            document.head.appendChild(sc);
        };

        // 1. Organization Schema (Professional Brand Identity)
        createJsonLd('org', {
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "Amigo Peludo",
            "url": "https://amigopeludo.cl",
            "logo": "https://amigopeludo.cl/icon.png",
            "sameAs": [
                "https://www.instagram.com/amigopeludo_oficial/",
                "https://www.tiktok.com/@amigopeludo_oficial"
            ],
            "contactPoint": {
                "@type": "ContactPoint",
                "contactType": "customer support",
                "availableLanguage": ["Spanish", "English"]
            }
        });

        // 2. Breadcrumbs (Improved Search Navigation)
        const pathSegments = new URL(url).pathname.split('/').filter(Boolean);
        if (pathSegments.length > 0) {
            const breadcrumbItems = pathSegments.map((segment, index) => ({
                "@type": "ListItem",
                "position": index + 2,
                "name": segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, ' '),
                "item": `https://amigopeludo.cl/${pathSegments.slice(0, index + 1).join('/')}`
            }));

            createJsonLd('breadcrumbs', {
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                "itemListElement": [
                    {
                        "@type": "ListItem",
                        "position": 1,
                        "name": "Inicio",
                        "item": "https://amigopeludo.cl"
                    },
                    ...breadcrumbItems
                ]
            });
        }

        // 3. Custom Page Schema
        if (jsonLd) {
            if (Array.isArray(jsonLd)) {
                jsonLd.forEach((item, i) => {
                    createJsonLd(`page-${i}`, {
                        "@context": "https://schema.org",
                        ...item
                    });
                });
            } else {
                createJsonLd('page', {
                    "@context": "https://schema.org",
                    ...jsonLd
                });
            }
        }

        // --- Canonical Link ---
        let canonical = document.querySelector('link[rel="canonical"]');
        if (!canonical) {
            canonical = document.createElement('link');
            canonical.setAttribute('rel', 'canonical');
            document.head.appendChild(canonical);
        }
        canonical.setAttribute('href', url);

        return () => {};
    }, [title, description, keywords, rawImage, url, type, jsonLd]);

    return null; // This component doesn't render anything UI-wise
};
